import classNames from 'classnames';
import { HTMLAttributes, useState } from 'react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { useMounted } from '@/shared/hooks/use-mounted';
import Popup from '../../shared/Popup';
import CloseButton from '../CloseButton';
import ConstructorButton from '../ConstructorButton';
import {
    cameraViewTypes,
    useSetStandPickerCameraViewState,
    useStandPickerCameraViewValue,
} from '../atoms/stand-picker-camera-view';
import { useMapStateContext } from './context';

type Spaces = 0 | 9 | 12 | 18 | 30 | 40;
type StandType = 'unset' | 'standart' | 'individual' | 'premium';

type FilterButton<T> = {
    text: string;
    value: T;
};

const MAP_FILTERS_NAME = 'map-filters';

const types: FilterButton<StandType>[] = [
    {
        text: 'Индивидуальный',
        value: 'individual',
    },
    {
        text: 'Премиальный',
        value: 'premium',
    },
    {
        text: 'Стандартный',
        value: 'standart',
    },
];

const standartSpaces: FilterButton<Spaces>[] = [
    {
        text: '9',
        value: 9,
    },
    {
        text: '12',
        value: 12,
    },
    {
        text: '18',
        value: 18,
    },
];

const premiumSpaces: FilterButton<Spaces>[] = [
    {
        text: '30',
        value: 30,
    },
    {
        text: '40',
        value: 40,
    },
];

type Props = HTMLAttributes<HTMLDivElement> & {
    cameraPosition?: boolean;
};
const MapFilters = ({ cameraPosition, ...props }: Props) => {
    const { space, setSpace, type, setType } = useMapStateContext();
    const [tempSpace, setTempSpace] = useState<Spaces>(0);
    const [tempType, setTempType] = useState<StandType>('unset');
    const spaces = type === 'standart' ? standartSpaces : type === 'premium' ? premiumSpaces : [];
    const tempSpaces = tempType === 'standart' ? standartSpaces : tempType === 'premium' ? premiumSpaces : [];
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';
    const { openPopup, closePopup } = useSetOpenedPopupsState();
    const standPickerCameraView = useStandPickerCameraViewValue();
    const setStandPickerCameraView = useSetStandPickerCameraViewState();
    const isMounted = useMounted();

    return isDesktop ? (
        <div {...props} className={classNames('map-filters', props.className)}>
            {isDesktop && isMounted && cameraPosition && (
                <div className="map-filter map-camera-view-controls">
                    <div className="map-filter-title text-m">Ракурс</div>

                    <ul className="pointer-events-auto list-unstyled inhouse-map-camera-view-controls">
                        {cameraViewTypes.map((item, i) => (
                            <li key={i} className="map-filter-list-item-wrapper">
                                <button
                                    className={classNames('inhouse-map-camera-view-control', {
                                        'is-active': item.value === standPickerCameraView,
                                    })}
                                    onClick={() => {
                                        setStandPickerCameraView(item.value);
                                    }}
                                >
                                    {item.text}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="map-filter">
                <div className="map-filter-title text-m">Тип стенда</div>
                <ul className="map-filter-list list-unstyled">
                    {types.map((item, i) => {
                        return (
                            <li key={i} className="map-filter-list-item-wrapper">
                                <button
                                    className={classNames('map-filter-list-item text-s', {
                                        'map-filter-list-item-active': item.value === type,
                                    })}
                                    onClick={() => {
                                        if (item.value === type) {
                                            setType('unset');
                                        } else {
                                            setType(item.value);
                                        }
                                        setSpace(0);
                                    }}
                                >
                                    {item.text}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
            {(type === 'standart' || type === 'premium') && (
                <div className="map-filter">
                    <div className="map-filter-title text-m">Площадь стенда</div>
                    <ul className="map-filter-list list-unstyled">
                        {spaces.map((item, i) => {
                            return (
                                <li key={i} className="map-filter-list-item-wrapper">
                                    <button
                                        className={classNames('map-filter-list-item text-s', {
                                            'map-filter-list-item-active': item.value === space,
                                        })}
                                        onClick={() => {
                                            if (item.value === space) {
                                                setSpace(0);
                                            } else {
                                                setSpace(item.value);
                                            }
                                        }}
                                    >
                                        {item.text}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    ) : (
        <div {...props} className={classNames('tablet-filters', props.className)}>
            <button
                className="map-filter-list-item text-s map-filter-list-item-active pointer-events-auto"
                onClick={() => {
                    openPopup(MAP_FILTERS_NAME);
                    setTempType(type);
                    setTempSpace(space);
                }}
            >
                Фильтр
            </button>
            <Popup overlay name={MAP_FILTERS_NAME} className="map-filters-popup" data-popup-preset="default">
                <div className="map-filters">
                    <div className="map-filters-popup-top">
                        <div className="map-filters-popup-top-title">Фильтр</div>
                        <CloseButton
                            variant="default"
                            className="map-filters-popup__close-btn"
                            onClick={() => {
                                closePopup(MAP_FILTERS_NAME);
                            }}
                        />
                    </div>
                    <div className="map-filter">
                        <div className="map-filter-title text-m">Тип стенда</div>
                        <ul className="map-filter-list list-unstyled">
                            {types.map((item, i) => {
                                return (
                                    <li key={i} className="map-filter-list-item-wrapper">
                                        <button
                                            className={classNames('map-filter-list-item text-s', {
                                                'map-filter-list-item-active': item.value === tempType,
                                            })}
                                            onClick={() => {
                                                if (item.value === tempType) {
                                                    setTempType('unset');
                                                } else {
                                                    setTempType(item.value);
                                                }
                                                setTempSpace(0);
                                            }}
                                        >
                                            {item.text}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    {(tempType === 'standart' || tempType === 'premium') && (
                        <div className="map-filter">
                            <div className="map-filter-title text-m">Площадь стенда</div>
                            <ul className="map-filter-list list-unstyled">
                                {tempSpaces.map((item, i) => {
                                    return (
                                        <li key={i} className="map-filter-list-item-wrapper">
                                            <button
                                                className={classNames('map-filter-list-item text-s', {
                                                    'map-filter-list-item-active': item.value === tempSpace,
                                                })}
                                                onClick={() => {
                                                    if (item.value === tempSpace) {
                                                        setTempSpace(0);
                                                    } else {
                                                        setTempSpace(item.value);
                                                    }
                                                }}
                                            >
                                                {item.text}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                    <div className="map-filter">
                        <div className="map-filter-title text-m">Ракурс</div>
                        <ul className="list-unstyled map-filter-list">
                            {cameraViewTypes.map((item, i) => (
                                <li key={i} className="map-filter-list-item-wrapper">
                                    <button
                                        className={classNames('map-filter-list-item text-s', {
                                            'map-filter-list-item-active': item.value === standPickerCameraView,
                                        })}
                                        onClick={() => {
                                            setStandPickerCameraView(item.value);
                                        }}
                                    >
                                        {item.text}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <ConstructorButton
                        className="filters-confirm-btn"
                        variant="blue"
                        onClick={() => {
                            setType(tempType);
                            setSpace(tempSpace);
                            closePopup(MAP_FILTERS_NAME);
                        }}
                    >
                        Применить
                    </ConstructorButton>
                </div>
            </Popup>
        </div>
    );
};

export default MapFilters;
